import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { TenantPage } from 'components/common';
import SectionTitle from 'components/common/SectionTitle';
import { IOption, ITransportActiveDocument } from 'interfaces';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { productService } from 'services/api';
import toast from 'features/toast';

import { DataGridPremium } from '@mui/x-data-grid-premium';
import { transportActiveDocumentService } from 'services/api/TransportActiveDocumentService';
import { transportDocumentActiveLineService } from 'services/api/TransportActiveDocumentLineService';
import { supplierTransportActiveDocumentService } from 'services/api/SupplierTransportActiveDocumentService';
import TransportDocumentActiveForm from '../TransportActiveDocumentForm';
import { GridColDef } from '@mui/x-data-grid-premium';
import { TransportActiveDocumentLinesColumns } from 'components/common/enhanced/common-headers/transport-active-document-lines';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TransportActiveDocumentLineForm from '../TransportActiveDocumentLineForm';
import SaveIcon from '@mui/icons-material/Save';
import CustomizedSteppers from 'components/common/TransportActiveDocumentStatus';
import UpdateTransportActiveDocumentLineDialog from '../UpdateTransportActiveDocumentLineDialog';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isDirector } from 'components/helpers/IsDirector';
import { supplierTransportDocumentActiveLineService } from 'services/api/SupplierTransportActiveDocumentLineService';
interface TransportActiveDocumentDetailProps {
  isSupplierDdt?: boolean; // optional, default false
}
export function TransportActiveDocumentDetail({
  isSupplierDdt = false
}: TransportActiveDocumentDetailProps) {
  const { t } = useTranslation();
  const prefix = 'pages.active-ddt.detail';
  const supplierPrefix = 'pages.supplier-active-ddt.detail';
  const { id } = useParams();
  const activeDocumentService = isSupplierDdt
    ? supplierTransportActiveDocumentService
    : transportActiveDocumentService;

  const activeDocumentLineService = isSupplierDdt
    ? supplierTransportDocumentActiveLineService
    : transportDocumentActiveLineService;

  const [ddt, setDdt] = useState<ITransportActiveDocument>();
  const [initialDdtLines, setInitialDdtLines] = useState<any>();

  const [productOptions, setProductOptions] = useState<IOption[]>([]);
  const [productToAdd, setProductToAdd] = useState<any>(null);
  const [isClosed, setIsClosed] = useState<boolean>(false);

  const [isStatusNew, _] = useState<boolean>(true);

  const isDisabledEdit = isTechnician();
  const isDir = isDirector();

  const [confirmFinalizeDialog, setConfirmFinalizeDialog] = useState(false);

  // ----------------------------------------------------------- GETTER AND USEFFECTS
  const getDDTById = () => {
    activeDocumentService.get(+id).then((res) => {
      setDdt(res);
      if (res.status === 'SENT') {
        setIsClosed(true);
      }
      setInitialDdtLines(res.lines);
    });
  };

  const getProductSummary = (ref?: string) => {
    if (!isSupplierDdt) {
      const warehouse_params = {
        warehouse_id: ddt.warehouse_out,
        // supplier: isSupplierDdt ? ddt.supplier : undefined, // commented out as it may not be needed
        status: 'ACTIVE',
        order: ref ? ref : ddt.order_ref ? ddt.order_ref : '',
        has_orders: true
      };
      productService.getAllBaseInformation(warehouse_params).then(setProductOptions);
    } else {
      const supplier_params: any = {
        // supplier: isSupplierDdt ? ddt.supplier : undefined, // commented out as it may not be needed
        status: 'ACTIVE',
        supplier: ddt.supplier
      };
      productService.getAllBaseInformation(supplier_params).then(setProductOptions);
    }
  };

  useEffect(() => {
    getDDTById();
  }, []);

  useEffect(() => {
    if (ddt && ddt.id) {
      getProductSummary();
    }
  }, [ddt?.id, productToAdd?.id]);

  // ---------------------------------------------------------- UPDATE-TRANSPORT-ACTIVE-DOCUMENT
  const updateDDT = (values: any) => {
    activeDocumentService
      .update(ddt.id, {
        ...values,
        warehouse_out:
          typeof values.warehouse_out === 'object'
            ? values.warehouse_out['id']
            : values.warehouse_out,
        warehouse_in:
          typeof values.warehouse_in === 'object' ? values.warehouse_in['id'] : values.warehouse_in
      })
      .then((res) => {
        if (res) {
          if ('warnings' in res) {
            res.warnings.map((warning) => {
              toast.warning(warning);
            });
          } else {
            toast.success(t(`${prefix}.updated`));
          }
          getDDTById();
          getProductSummary(res.order);
        }
      });
  };

  // ---------------------------------------------------------- TRASFERIMENTO-LINE-LOGIC
  const [editTransportActiveDocumentLineRow, setEditTransportActiveDocumentLineRow] =
    useState<any>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);

  // ---------------------------------------------------------  EDIT LINE
  const setOpenDialog = (row: any) => {
    setEditTransportActiveDocumentLineRow(row);
    setOpenEditDialog(true);
  };

  const setCloseDialog = () => {
    setOpenEditDialog(false);
  };

  const onSubmitEdit = async (id, params) => {
    activeDocumentLineService.update(id, params).then(() => {
      toast.success('Updated successfully');
    });
    const updatedLines = ddt.lines.map((line) => {
      if (line.id === id) {
        return { ...line, ...params };
      }
      return line;
    });
    setDdt({ ...ddt, lines: updatedLines });
    setCloseDialog();
  };

  // ---------------------------------------------------------- DELETE-LINE
  const changesToSave = (): boolean => {
    return JSON.stringify(initialDdtLines) === JSON.stringify(ddt.lines);
  };

  const handleDelete = (id: number | null) => {
    if (changesToSave()) {
      activeDocumentLineService.delete(id).then((res) => {
        if (res && res.status === 204) {
          toast.success(t(`${prefix}.line-deleted-success`));
        } else {
          toast.error(t(`${prefix}.line-deleted-error`));
        }
      });
    }
    getDDTById();
  };

  const saveDDTLines = () => {
    activeDocumentLineService.bulkCreate(ddt.id, ddt.lines).then((res) => {
      if (res.status === 201) {
        if (res.data?.warnings) {
          res.data.warnings.forEach((warning: string) => toast.warning(warning));
        } else {
          toast.success(res.data.detail);
        }
      } else {
        toast.error(t(`${prefix}.line-add-error`));
      }
    });
    setInitialDdtLines(ddt.lines);
    setTimeout(() => {
      getDDTById();
    }, 3000);
  };

  // ---------------------------------------------------------- GENERATE-TMP-DOCUMENT

  const generateTmpDdt = () => {
    activeDocumentService.generateTmpPdf(ddt.id).then((res) => {
      toast.success(t(`${prefix}.pdf-tmp-generated`));
    });
  };

  const downloadTmpDdt = () => {
    activeDocumentService.downloadTmpPdf(ddt.id);
  };

  // ---------------------------------------------------------- CONFIRM-FINALIZE

  const openConfirmfinalizeDialog = () => {
    setConfirmFinalizeDialog(true);
  };

  const closeConfirmfinalizeDialog = () => {
    setConfirmFinalizeDialog(false);
  };

  const finalizeDdt = () => {
    activeDocumentService.finalize(ddt.id).then((res) => {
      if (res?.warnings?.length) {
        res.warnings.forEach((warning) => {
          if (warning?.batches) {
            toast.warning(t('alerts.earlier-expiration', { batches: warning.batches.join(', ') }));
          } else {
            toast.warning(warning.type);
          }
        });
      } else if (res?.error) {
        toast.error(res.error);
      } else {
        if (isSupplierDdt) {
          toast.success(t(`${supplierPrefix}.finalized`));
        } else {
          toast.success(t(`${prefix}.finalized`));
        }
      }
      getDDTById();
    });
    closeConfirmfinalizeDialog();
  };

  // ---------------------------------------------------------- COLUMNS
  const columns: GridColDef[] = TransportActiveDocumentLinesColumns(
    isStatusNew,
    handleDelete,
    setOpenDialog,
    isDisabledEdit,
    isClosed
  );

  const onSubmitRow = (values: any) => {
    if (values.quantity > 0) {
      const product_batch_code = values['product_batch'].product_batch ?? 'NO_BATCH';
      const expiration_date = values['product_batch'].expiration_date ?? '';
      const location_code = values['product_batch']?.location ?? null;
      values['product_code'] = values['product'].code;
      values['product_description'] = values['product'].description.replace(
        //correzione descrizion IOption
        values['product'].code + ' - ',
        ''
      );
      values['expiration_date'] = expiration_date;
      values['location'] = location_code;
      values['product_batch'] = product_batch_code;
      values['document'] = ddt.id;
      values['notes'] = location_code ?? '';
      values['id'] = uuidv4();
      const updatedLines = [...ddt.lines, values];
      setDdt({ ...ddt, lines: updatedLines });
    } else {
      toast.warning(t(`${prefix}`));
    }
  };

  return (
    <TenantPage
      title={
        isSupplierDdt ? t(`${supplierPrefix}.title`) + ' ' + id : t(`${prefix}.title`) + ' ' + id
      }
      subtitle={t(`${prefix}.subtitle`)}
      menuRight={
        <div className="flex items-center justify-end">
          <div className="w-60">
            <CustomizedSteppers status={ddt?.status}></CustomizedSteppers>
          </div>
          {ddt && (
            <ButtonGroup size="small" variant="outlined">
              {!isDisabledEdit && !isDir && (
                <Button disabled={!changesToSave()} color="primary" onClick={generateTmpDdt}>
                  {t(`${prefix}.generate-tmp`)}
                </Button>
              )}
              <Button disabled={!changesToSave()} color="primary" onClick={downloadTmpDdt}>
                {t(`${prefix}.download-tmp`)}
              </Button>
            </ButtonGroup>
          )}
          {ddt && ddt.status === 'NEW' && !isDisabledEdit && (
            <ButtonGroup size="small" variant="outlined">
              <Button disabled={changesToSave()} onClick={saveDDTLines}>
                <Tooltip title={t('global.save')}>
                  <SaveIcon />
                </Tooltip>
              </Button>
              {!isDir && (
                <Button
                  disabled={!changesToSave()}
                  color="success"
                  onClick={openConfirmfinalizeDialog}>
                  {t(`${prefix}.finalize`)}
                </Button>
              )}
            </ButtonGroup>
          )}
        </div>
      }
      entityCode={ddt?.ref}>
      <div>
        <div className="mb-4">
          <SectionTitle>
            {isSupplierDdt ? t(`${supplierPrefix}.header`) : t(`${prefix}.header`)}
          </SectionTitle>
          <div className="w-full my-4">
            {ddt && (
              <TransportDocumentActiveForm
                onSubmit={updateDDT}
                transport_document={ddt}
                isUpdate={true}
                disableEdit={isDisabledEdit}
                isDirector={isDir}
                isSupplierDdt={isSupplierDdt}
              />
            )}
          </div>
        </div>

        <div>
          {ddt && !isDisabledEdit && !isClosed && (
            <Accordion className="bg-slate-50 my-8">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t(`${prefix}.add-element`)}</Typography>
              </AccordionSummary>

              <AccordionDetails>
                <TransportActiveDocumentLineForm
                  ddt={ddt}
                  onSubmit={onSubmitRow}
                  products={productOptions}
                  setProduct={setProductToAdd}
                />
              </AccordionDetails>
            </Accordion>
          )}
          <div className="w-full p-4 " style={{ height: 300, width: '100%' }}>
            <DataGridPremium
              density="compact"
              initialState={{
                pinnedColumns: {
                  right: ['actions']
                }
              }}
              rows={ddt?.lines ?? []}
              sx={{ border: 'none' }}
              disableSelectionOnClick
              pagination
              columns={columns}
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
            />
          </div>
        </div>

        {/* Dialog component to confirm finalization of the transport active document */}
        <Dialog
          open={confirmFinalizeDialog}
          onClose={closeConfirmfinalizeDialog}
          sx={{ '& .MuiDialog-paper': { width: '800px' } }}>
          <DialogTitle>{t(`${prefix}.finalize-dialog.title`)}</DialogTitle>
          <DialogContent>{t(`${prefix}.finalize-dialog.message`)}</DialogContent>
          {/* Dialog actions containing cancel and confirm buttons */}
          <DialogActions>
            {/* Button to close the dialog without finalizing the transport active document */}
            <Button onClick={closeConfirmfinalizeDialog} color="error">
              {t('actions.cancel')}
            </Button>
            {/* Button to finalize the transport active document */}
            <Button onClick={finalizeDdt}>{t('actions.confirm')}</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog component to edit contract line details */}
        <Dialog open={openEditDialog} onClose={setCloseDialog} fullWidth maxWidth="md">
          <DialogTitle id="alert-dialog-title">{t(`pages.contract-line.edit.title`)}</DialogTitle>
          <DialogContent>
            <UpdateTransportActiveDocumentLineDialog
              trasportActiveDocumentLine={editTransportActiveDocumentLineRow}
              ddt={ddt}
              onSubmit={onSubmitEdit}
              closeDialog={setCloseDialog}
            />
          </DialogContent>
          {/* Dialog actions containing cancel and confirm buttons */}
          <DialogActions>
            {/* Button to close the dialog without saving changes */}
            <Button onClick={setCloseDialog} color="error">
              {t('actions.cancel')}
            </Button>
            {/* Button to submit the form contained within the dialog */}
            <Button type="submit" form="updateTransportActiveDocumentLine">
              {t(`actions.confirm`)}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Dialog component to confirm finalization of the transport active document */}
        <Dialog
          open={confirmFinalizeDialog}
          onClose={closeConfirmfinalizeDialog}
          sx={{ '& .MuiDialog-paper': { width: '800px' } }}>
          <DialogTitle>
            {isSupplierDdt
              ? t(`${supplierPrefix}.finalize-dialog.title`)
              : t(`${prefix}.finalize-dialog.title`)}
          </DialogTitle>
          <DialogContent>
            {isSupplierDdt
              ? t(`${supplierPrefix}.finalize-dialog.message`)
              : t(`${prefix}.finalize-dialog.message`)}
          </DialogContent>
          {/* Dialog actions containing cancel and confirm buttons */}
          <DialogActions>
            {/* Button to close the dialog without finalizing the transport active document */}
            <Button onClick={closeConfirmfinalizeDialog} color="error">
              {t('actions.cancel')}
            </Button>
            {/* Button to finalize the transport active document */}
            <Button onClick={finalizeDdt}>{t('actions.confirm')}</Button>
          </DialogActions>
        </Dialog>
      </div>
    </TenantPage>
  );
}

export default TransportActiveDocumentDetail;
